import React, { useState, useEffect } from 'react';
import { toLocalYMD } from '../lib/dates';
import { X, Briefcase, MapPin, PoundSterling, Calendar, Search, User, Check, Loader2, FileText, Trash2 } from 'lucide-react';
import { persistCustomService } from '../utils/persistCustomService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewJobModal = ({ onClose, onSave }) => {
  // Customer selection
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Services
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  
  // Job form state
  const [jobService, setJobService] = useState('');
  const [jobServicePrices, setJobServicePrices] = useState('');
  const [jobPrice, setJobPrice] = useState('');
  const [jobFrequency, setJobFrequency] = useState('6');
  const [jobDate, setJobDate] = useState('');
  const [jobAddress, setJobAddress] = useState('');
  // New: customer-facing invoice notes + pricing-mode toggle (mirrors the
  // Edit Job modal so both flows persist the same fields).
  const [jobInvoiceNotes, setJobInvoiceNotes] = useState('');
  const [pricingMode, setPricingMode] = useState('flat'); // 'flat' | 'lines'
  const [lineItems, setLineItems] = useState([]);
  // Squeegee-style: price the first appointment separately from recurring visits
  const [splitFirstPrice, setSplitFirstPrice] = useState(false);
  const [firstCleanPrice, setFirstCleanPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dayHint, setDayHint] = useState(null);

  // Best-day hint: rank upcoming days by proximity to this customer's address.
  useEffect(() => {
    setDayHint(null);
    if (!selectedCustomer?.id) return;
    let stale = false;
    (async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/optimizer/place-customer`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer_id: selectedCustomer.id }),
        });
        if (!res.ok) return;
        const d = await res.json();
        if (!stale && d.resolved && d.suggestions?.length) setDayHint(d.suggestions[0]);
      } catch { /* hint is optional */ }
    })();
    return () => { stale = true; };
  }, [selectedCustomer]);

  // Fetch services on mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/services`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setServices(data);
          // Don't auto-prefill — the user explicitly picks a service via the
          // "+ Add a service" dropdown. Pre-filling the first alphabetical
          // service was confusing (jobs landed as "Annex Clean" by default).
        }
      } catch (err) {
        console.error('Error fetching services:', err);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  // Search customers with debounce
  useEffect(() => {
    if (!customerSearch || customerSearch.length < 2) {
      setCustomers([]);
      return;
    }
    
    const timeoutId = setTimeout(async () => {
      setLoadingCustomers(true);
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/customers?search=${encodeURIComponent(customerSearch)}&limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCustomers(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Error searching customers:', err);
      } finally {
        setLoadingCustomers(false);
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [customerSearch]);

  // Auto-fill address when customer is selected
  useEffect(() => {
    if (selectedCustomer?.address) {
      setJobAddress(selectedCustomer.address);
    }
  }, [selectedCustomer]);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch('');
    setCustomers([]);
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }
    
    // Compute total. When in line-items mode, sum the breakdown; otherwise
    // fall back to summing the legacy per-service prices.
    const cleanLineItems = lineItems
      .map(li => ({
        service: (li.service || '').trim(),
        rate: parseFloat(li.rate) || 0,
        quantity: parseFloat(li.quantity) || 1,
      }))
      .filter(li => li.service);
    const usingLines = pricingMode === 'lines' && cleanLineItems.length > 0;
    const linesTotal = cleanLineItems.reduce((s, li) => s + li.rate * li.quantity, 0);
    const legacyTotal = (jobServicePrices || '').split(',').reduce(
      (sum, p) => sum + (parseFloat(p) || 0), 0
    );
    const totalPrice = usingLines ? linesTotal : legacyTotal;
    
    if (totalPrice <= 0) {
      setError(usingLines
        ? 'Add at least one service with a rate'
        : 'Please enter prices for all services');
      return;
    }

    const useInitialPrice = splitFirstPrice && !usingLines && jobFrequency !== '0';
    const initialPriceNum = parseFloat(firstCleanPrice) || 0;
    if (useInitialPrice && initialPriceNum <= 0) {
      setError('Enter a price for the first appointment');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const scheduledDate = jobDate || toLocalYMD(new Date());
      const isOneOff = jobFrequency === '0';
      
      const jobData = {
        customer_id: selectedCustomer.id,
        customer_name: selectedCustomer.name,
        customer_address: jobAddress || selectedCustomer.address,
        // When lines mode, derive `service` from the first line so
        // dashboards / search show something sensible; the breakdown is
        // the source of truth via `line_items`.
        service: usingLines
          ? cleanLineItems.map(li => li.service).join(', ')
          : jobService,
        services: usingLines
          ? cleanLineItems.map(li => li.service).join(', ')
          : jobService,
        service_prices: usingLines
          ? cleanLineItems.map(li => (li.rate * li.quantity).toFixed(2)).join(',')
          : jobServicePrices,
        price: totalPrice,
        frequency: isOneOff ? null : jobFrequency,
        scheduled_date: scheduledDate,
        status: 'scheduled',
        address: jobAddress || selectedCustomer.address,
        zone: selectedCustomer.zone,
        is_one_off: isOneOff,
        invoice_notes: jobInvoiceNotes,
        pricing_mode: usingLines ? 'lines' : 'flat',
        line_items: usingLines ? cleanLineItems : [],
        ...(useInitialPrice ? {
          price: initialPriceNum,
          initial_price: initialPriceNum,
          recurring_price: totalPrice,
          recurring_service_prices: jobServicePrices,
          service_prices: '',
        } : {}),
      };
      
      const response = await fetch(`${API_URL}/api/jobs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jobData)
      });
      
      if (response.ok) {
        const createdJob = await response.json();
        if (onSave) onSave(createdJob);
        onClose();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create job');
      }
    } catch (err) {
      console.error('Error creating job:', err);
      setError('Error creating job. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 safe-area-all"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md modal-shell-h overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Briefcase size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold">Add New Job</h2>
                {selectedCustomer && (
                  <p className="text-white/80 text-sm">for {selectedCustomer.name}</p>
                )}
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X size={22} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}
          
          {/* Customer Selection */}
          {!selectedCustomer ? (
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Select Customer *
              </label>
              <div className="relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="Search customers..."
                  className="w-full pl-11 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                />
                {loadingCustomers && (
                  <Loader2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 animate-spin" />
                )}
              </div>
              
              {/* Customer results */}
              {customers.length > 0 && (
                <div className="mt-2 border border-zinc-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                  {customers.map(customer => (
                    <button
                      key={customer.id}
                      onClick={() => handleSelectCustomer(customer)}
                      className="w-full px-4 py-3 text-left hover:bg-zinc-50 border-b border-zinc-100 last:border-0 flex items-center gap-3"
                    >
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <User size={18} className="text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-zinc-900 truncate">{customer.name}</p>
                        <p className="text-sm text-zinc-500 truncate">{customer.address?.split(',')[0]}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {customerSearch.length >= 2 && customers.length === 0 && !loadingCustomers && (
                <p className="text-sm text-zinc-500 mt-2">No customers found</p>
              )}
            </div>
          ) : (
            <>
              {/* Selected customer card */}
              <div className="bg-zinc-50 rounded-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <User size={20} className="text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-zinc-900">{selectedCustomer.name}</p>
                  <p className="text-sm text-zinc-500">{selectedCustomer.address?.split(',')[0]}</p>
                </div>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
                >
                  <X size={18} className="text-zinc-500" />
                </button>
              </div>

              {/* Service Type + Pricing — Squeegee-style tabbed picker */}
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Services & Price</label>
                {/* Mode toggle */}
                <div className="flex bg-zinc-100 rounded-xl p-1 mb-3" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={pricingMode !== 'lines'}
                    onClick={() => setPricingMode('flat')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      pricingMode !== 'lines' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                    data-testid="newjob-pricing-mode-flat"
                  >
                    Price Job
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={pricingMode === 'lines'}
                    onClick={() => {
                      // Seed line items from the existing per-service rows
                      // (if any) so switching tabs doesn't lose state.
                      if (lineItems.length === 0) {
                        const svcs = (jobService || '').split(',').map(s => s.trim()).filter(Boolean);
                        const prices = (jobServicePrices || '').split(',');
                        const seeded = svcs.length > 0
                          ? svcs.map((s, i) => ({ service: s, rate: parseFloat(prices[i]) || 0, quantity: 1 }))
                          : [{ service: '', rate: 0, quantity: 1 }];
                        setLineItems(seeded);
                      }
                      setPricingMode('lines');
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      pricingMode === 'lines' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                    data-testid="newjob-pricing-mode-lines"
                  >
                    Price Services
                  </button>
                </div>

                {loadingServices ? (
                  <div className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl flex items-center gap-2 text-zinc-400">
                    <Loader2 size={16} className="animate-spin" />
                    Loading services...
                  </div>
                ) : pricingMode === 'lines' ? (
                  // ----- Lines mode (per-service breakdown with qty) -----
                  <div className="space-y-2" data-testid="newjob-line-items-editor">
                    {lineItems.map((li, idx) => {
                      const lineTotal = (parseFloat(li.rate) || 0) * (parseFloat(li.quantity) || 1);
                      return (
                        <div key={idx} className="bg-zinc-50 rounded-xl p-3 space-y-2" data-testid={`newjob-line-item-${idx}`}>
                          <div className="flex items-start gap-2">
                            <select
                              value={li.service}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '__custom__') {
                                  const typed = (window.prompt('New service name:') || '').trim();
                                  if (!typed) return;
                                  persistCustomService(typed, services).then(saved => {
                                    if (saved) setServices(prev => [...prev, saved]);
                                  });
                                  const next = [...lineItems];
                                  next[idx] = { ...next[idx], service: typed };
                                  setLineItems(next);
                                  return;
                                }
                                const next = [...lineItems];
                                const svc = services.find(s => s.name === val);
                                next[idx] = {
                                  ...next[idx],
                                  service: val,
                                  rate: (!next[idx].rate && svc?.default_price)
                                    ? Number(svc.default_price) : next[idx].rate,
                                };
                                setLineItems(next);
                              }}
                              className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-zinc-300 text-sm focus:border-emerald-500 outline-none"
                            >
                              <option value="">Choose service…</option>
                              {services.map(s => (
                                <option key={s.id} value={s.name}>{s.name}</option>
                              ))}
                              {li.service && !services.find(s => s.name === li.service) && (
                                <option value={li.service}>{li.service}</option>
                              )}
                              <option value="__custom__">+ Add new (type your own)…</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => setLineItems(lineItems.filter((_, i) => i !== idx))}
                              className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              aria-label="Remove line"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <div className="grid grid-cols-3 gap-2 items-end">
                            <div>
                              <label className="block text-[11px] text-zinc-500 mb-1">Rate (£)</label>
                              <input
                                type="number" step="0.01" min="0"
                                value={li.rate}
                                onChange={(e) => {
                                  const next = [...lineItems];
                                  next[idx] = { ...next[idx], rate: e.target.value };
                                  setLineItems(next);
                                }}
                                className="w-full px-2 py-1.5 rounded-lg border border-zinc-300 text-sm focus:border-emerald-500 outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] text-zinc-500 mb-1">Qty</label>
                              <input
                                type="number" step="1" min="1"
                                value={li.quantity}
                                onChange={(e) => {
                                  const next = [...lineItems];
                                  next[idx] = { ...next[idx], quantity: e.target.value };
                                  setLineItems(next);
                                }}
                                className="w-full px-2 py-1.5 rounded-lg border border-zinc-300 text-sm focus:border-emerald-500 outline-none"
                              />
                            </div>
                            <div className="text-right">
                              <label className="block text-[11px] text-zinc-500 mb-1">Total</label>
                              <div className="font-semibold text-zinc-900 text-sm py-1.5">
                                £{lineTotal.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setLineItems([...lineItems, { service: '', rate: 0, quantity: 1 }])}
                      className="w-full py-2 border-2 border-dashed border-zinc-300 rounded-xl text-sm font-medium text-zinc-600 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
                      data-testid="newjob-line-item-add"
                    >
                      + Add service
                    </button>
                    <div className="flex justify-between items-center p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                      <span className="font-semibold text-emerald-700">Total</span>
                      <span className="font-bold text-emerald-700" data-testid="newjob-lines-total">
                        £{lineItems.reduce((s, li) => s + (parseFloat(li.rate) || 0) * (parseFloat(li.quantity) || 1), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : (
                  // ----- Flat-price mode (legacy multi-service rows) -----
                  <div className="space-y-2">
                    {/* Selected services with individual prices */}
                    {(jobService || '').split(',').filter(s => s.trim()).map((svc, idx) => {
                      const prices = (jobServicePrices || '').split(',');
                      const price = prices[idx] || '';
                      return (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-zinc-50 rounded-lg border border-zinc-200">
                          <span className="flex-1 text-sm font-medium text-zinc-700">{svc.trim()}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-zinc-400">£</span>
                            <input
                              type="number"
                              step="0.01"
                              value={price}
                              onChange={(e) => {
                                const newPrices = [...prices];
                                newPrices[idx] = e.target.value;
                                setJobServicePrices(newPrices.join(','));
                              }}
                              className="w-20 px-2 py-1 rounded border border-zinc-300 text-sm text-right"
                              placeholder="0.00"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const currentServices = (jobService || '').split(',').filter(s => s.trim());
                              const currentPrices = (jobServicePrices || '').split(',');
                              currentServices.splice(idx, 1);
                              currentPrices.splice(idx, 1);
                              setJobService(currentServices.join(', '));
                              setJobServicePrices(currentPrices.join(','));
                            }}
                            className="p-1 text-zinc-400 hover:text-red-500"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      );
                    })}
                    
                    {/* Total */}
                    {(jobService || '').trim() && (
                      <div className="flex justify-between items-center p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                        <span className="font-semibold text-emerald-700">Total</span>
                        <span className="font-bold text-emerald-700">
                          £{(jobServicePrices || '').split(',').reduce((sum, p) => sum + (parseFloat(p) || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    
                    {/* Service dropdown to add. "Add new…" lets the user
                        type a one-off service without leaving the modal. */}
                    <select
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        let svcName = val;
                        if (val === '__custom__') {
                          const typed = (window.prompt('New service name:') || '').trim();
                          if (!typed) return;
                          svcName = typed;
                          persistCustomService(typed, services).then(saved => {
                            if (saved) setServices(prev => [...prev, saved]);
                          });
                        }
                        const currentServices = (jobService || '').split(',').filter(s => s.trim());
                        const currentPrices = (jobServicePrices || '').split(',').filter((p, i) => currentServices[i]);
                        if (!currentServices.includes(svcName)) {
                          currentServices.push(svcName);
                          currentPrices.push('');
                          setJobService(currentServices.join(', '));
                          setJobServicePrices(currentPrices.join(','));
                        }
                      }}
                      className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="job-service-select"
                    >
                      <option value="">+ Add a service</option>
                      {services.map(service => (
                        <option key={service.id} value={service.name}>
                          {service.name}
                        </option>
                      ))}
                      <option value="__custom__">+ Add new (type your own)…</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Squeegee-style: separate first-appointment price (flat mode, recurring only) */}
              {pricingMode !== 'lines' && jobFrequency !== '0' && (
                <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={splitFirstPrice}
                      onChange={(e) => setSplitFirstPrice(e.target.checked)}
                      className="w-4 h-4 accent-sky-600"
                      data-testid="newjob-split-first-price-toggle"
                    />
                    <span className="text-sm font-semibold text-zinc-700">Price first appointment separately</span>
                  </label>
                  {splitFirstPrice && (
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-500">£</span>
                        <input
                          type="number"
                          step="0.01"
                          value={firstCleanPrice}
                          onChange={(e) => setFirstCleanPrice(e.target.value)}
                          placeholder="e.g. 25.00"
                          className="w-28 px-2 py-2 rounded-lg border border-sky-300 text-sm text-right bg-white"
                          data-testid="newjob-first-clean-price-input"
                        />
                        <span className="text-xs text-zinc-500">first clean price</span>
                      </div>
                      <p className="text-[11px] text-zinc-500 mt-1">
                        The first visit is charged at this price. All future visits use the service prices above.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Job Address */}
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Job Address</label>
                <div className="relative">
                  <MapPin size={18} className="absolute left-4 top-4 text-zinc-400" />
                  <textarea
                    value={jobAddress}
                    onChange={(e) => setJobAddress(e.target.value)}
                    placeholder="Enter job address"
                    rows={2}
                    className="w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-1">Auto-filled from customer. Edit if job is at a different location.</p>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Frequency</label>
                <select
                  value={jobFrequency}
                  onChange={(e) => setJobFrequency(e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="6">6 Weekly</option>
                  <option value="12">12 Weekly</option>
                  <option value="0">One-off</option>
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">
                  {jobFrequency === '0' ? 'Job Date *' : 'First Clean Date'}
                </label>
                <div className="relative">
                  <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="date"
                    value={jobDate}
                    onChange={(e) => setJobDate(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                {jobFrequency === '0' ? (
                  <p className="text-xs text-amber-600 mt-1">One-off job - will only appear on this date</p>
                ) : (
                  <p className="text-xs text-zinc-500 mt-1">Leave blank to add to schedule later</p>
                )}
                {dayHint && (
                  <button
                    type="button"
                    onClick={() => setJobDate(dayHint.date)}
                    data-testid="day-hint-btn"
                    className={`mt-2 w-full text-left px-3 py-2.5 rounded-xl border text-xs transition-colors ${
                      jobDate === dayHint.date
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                        : 'bg-indigo-50 border-indigo-200 text-indigo-800 hover:bg-indigo-100'
                    }`}
                  >
                    <span className="font-bold">
                      {jobDate === dayHint.date ? '✓ Best day selected' : 'Best day nearby:'}{' '}
                      {new Date(dayHint.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </span>
                    <span className="block mt-0.5">
                      {dayHint.nearest_mi} mi from {dayHint.nearest_customer_name}
                      {dayHint.nearby_count > 1 ? ` · ${dayHint.nearby_count} jobs nearby that day` : ''}
                      {jobDate !== dayHint.date ? ' — tap to use' : ''}
                    </span>
                  </button>
                )}
              </div>

              {/* Notes for invoice — customer-facing. Renders on the invoice
                  PDF, email and public invoice page. Kept distinct from any
                  internal worker notes (the job-edit modal has those). */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-zinc-700 mb-2">
                  <FileText size={16} className="text-amber-500" />
                  Notes for invoice
                  <span className="text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Customer-facing</span>
                </label>
                <textarea
                  value={jobInvoiceNotes}
                  onChange={(e) => setJobInvoiceNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. Cleaned inside conservatory glass · Next visit due 6 weeks"
                  className="w-full px-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  data-testid="newjob-invoice-notes-input"
                />
                <p className="text-[11px] text-zinc-400 mt-1">Will appear on the invoice the customer receives.</p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2 flex gap-3 flex-shrink-0 border-t border-zinc-100">
          <button
            onClick={onClose}
            data-testid="newjob-cancel-btn"
            className="flex-1 py-3 border border-zinc-200 rounded-xl font-medium text-zinc-600 hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            data-testid="newjob-submit-btn"
            disabled={saving || !selectedCustomer || (() => {
              // Disable when no usable price configured:
              //   • Flat mode → no sum of service_prices
              //   • Lines mode → no line item with a positive rate*qty
              const linesTotal = lineItems.reduce(
                (s, li) => s + (parseFloat(li.rate) || 0) * (parseFloat(li.quantity) || 1), 0
              );
              const flatTotal = (jobServicePrices || '').split(',').reduce(
                (s, p) => s + (parseFloat(p) || 0), 0
              );
              return pricingMode === 'lines' ? linesTotal <= 0 : flatTotal <= 0;
            })()}
            className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check size={18} />
                Add Job
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewJobModal;
